import React from "react";
import { Form, Input, Button, Card, Tag, Tooltip, Typography } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { icons } from "lucide-react";
import { defaultValues } from "./defaults";
import { LucideIconPicker } from "./LucideIconPicker";
import type { SectionMeta } from "./types";

const { Text } = Typography;

export const meta: SectionMeta = {
  key: "values",
  label: "Core Values",
  icon: null,
  color: "#f43f5e",
  desc: "Your company's guiding principles",
};

export const defaultData = defaultValues;

const DynamicIcon = ({ name, size }: { name: string; size?: number }) => {
  const IconComp =
    name && name in icons ? icons[name as keyof typeof icons] : null;
  return IconComp ? <IconComp size={size ?? 12} /> : null;
};

export const Preview: React.FC<{ data: any[] }> = ({ data }) => {
  if (data.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {data.map((item: any, i: number) => (
        <Tooltip key={i} title={item.description}>
          <Tag
            color="red"
            className="!m-0 !text-xs !leading-5 !font-medium !px-2"
          >
            {item.icon && <DynamicIcon name={item.icon} size={12} />}
            <span className="ml-1">{item.title}</span>
          </Tag>
        </Tooltip>
      ))}
    </div>
  );
};

export const FormFields: React.FC = () => (
  <Form.List name="values">
    {(fields, { add, remove }) => (
      <>
        {fields.map((field) => (
          <Card
            key={field.key}
            className="!mb-3"
            size="small"
            type="inner"
            extra={
              <Button
                type="text"
                danger
                size="small"
                onClick={() => remove(field.name)}
                icon={<DeleteOutlined />}
              />
            }
          >
            <div className="grid grid-cols-3 gap-3">
              <Form.Item
                {...field}
                name={[field.name, "icon"]}
                label="Icon"
                className="!mb-0"
                rules={[{ required: true, message: "Required" }]}
              >
                <LucideIconPicker />
              </Form.Item>
              <Form.Item
                {...field}
                name={[field.name, "title"]}
                label="Title"
                className="!mb-0"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input placeholder="e.g. Customer First" />
              </Form.Item>
              <Form.Item
                {...field}
                name={[field.name, "description"]}
                label="Description"
                className="!mb-0"
                rules={[{ required: true, message: "Required" }]}
              >
                <Input.TextArea rows={2} placeholder="Describe this value..." />
              </Form.Item>
            </div>
          </Card>
        ))}
        <Button
          type="dashed"
          block
          onClick={() => add()}
          icon={<PlusOutlined />}
          className="!mb-1"
        >
          Add Value
        </Button>
      </>
    )}
  </Form.List>
);
